import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useMatches } from "react-router-dom";

import type { AppLayoutVariant } from "~/components/app-layout";
import type { SystemHomeTab } from "~/systems/feature/system-home-route";

export type SystemHomeContentLayout = "padded" | "full";

export interface SystemHomeRouteHandle {
  layout?: AppLayoutVariant;
  contentLayout?: SystemHomeContentLayout;
  tab?: SystemHomeTab;
}

export function systemHomeHandle(handle: SystemHomeRouteHandle): SystemHomeRouteHandle {
  return handle;
}

export function useSystemHomeLayout(): AppLayoutVariant {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i].handle;
    if (handle && typeof handle === "object" && "layout" in handle && isAppLayoutVariant(handle.layout)) {
      return handle.layout;
    }
  }
  return "full";
}

export function useSystemHomeContentLayout(): SystemHomeContentLayout {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i].handle;
    if (
      handle &&
      typeof handle === "object" &&
      "contentLayout" in handle &&
      isSystemHomeContentLayout(handle.contentLayout)
    ) {
      return handle.contentLayout;
    }
  }
  return "padded";
}

const APP_LAYOUT_VARIANTS: readonly AppLayoutVariant[] = [
  "standard",
  "full",
  "centered",
  "narrow",
  "wide",
  "dashboard",
];

function isAppLayoutVariant(value: unknown): value is AppLayoutVariant {
  return typeof value === "string" && (APP_LAYOUT_VARIANTS as readonly string[]).includes(value);
}

function isSystemHomeContentLayout(value: unknown): value is SystemHomeContentLayout {
  return value === "padded" || value === "full";
}

interface CrPaths {
  list: (systemId: string) => string;
  detail: (systemId: string, id: string) => string;
  create: (systemId: string, opts?: { sourceBranchId?: string; targetBranchId?: string }) => string;
}

interface SystemRoutePathsContextValue {
  crPaths: CrPaths;
}

const SystemRoutePathsContext = createContext<SystemRoutePathsContextValue | null>(null);

export function useSystemRoutePaths() {
  const ctx = useContext(SystemRoutePathsContext);
  if (!ctx) {
    throw new Error("useSystemRoutePaths must be used within a SystemRoutePathsProvider.");
  }
  return ctx;
}

interface SystemRoutePathsProviderProps {
  basePath: string;
  children: ReactNode;
}

export function SystemRoutePathsProvider({ basePath, children }: SystemRoutePathsProviderProps) {
  const crPaths: CrPaths = {
    list: (systemId) => `${basePath}/${systemId}/change-requests`,
    detail: (systemId, id) => `${basePath}/${systemId}/change-requests/${id}`,
    create: (systemId, opts) => {
      const params = new URLSearchParams();
      if (opts?.sourceBranchId) params.set("sourceBranchId", opts.sourceBranchId);
      if (opts?.targetBranchId) params.set("targetBranchId", opts.targetBranchId);
      const qs = params.toString();
      return `${basePath}/${systemId}/change-requests/new${qs ? `?${qs}` : ""}`;
    },
  };

  return <SystemRoutePathsContext.Provider value={{ crPaths }}>{children}</SystemRoutePathsContext.Provider>;
}
